import React, {
  ForwardedRef,
  ForwardRefExoticComponent,
  FunctionComponent,
  PropsWithChildren,
  RefAttributes,
} from 'react'
import { createLocaleHook } from '../hooks/use-locale'
import {
  PrefixedLocaleFunction,
  TranslationPrefix,
} from '../resources/locale/locale'

type GetProps<Props, DefaultProps> = Omit<Props, keyof DefaultProps> & {
  [key in keyof DefaultProps]: key extends keyof Props
    ? NonNullable<Props[key]>
    : never
} extends infer U
  ? { [key in keyof U]: U[key] }
  : never

interface Factory<
  Props extends object = {},
  DefaultProps extends Partial<Props> = {},
  Lifecycle extends object | void = void,
  Prefix extends TranslationPrefix | undefined = undefined,
> extends ForwardRefExoticComponent<
    PropsWithChildren<Props> & RefAttributes<Props>
  > {
  withLocale: <_Locale extends TranslationPrefix>(
    prefix: _Locale,
  ) => Factory<Props, DefaultProps, Lifecycle, _Locale>
  withLifecycle: <_Lifecycle extends object | void>(
    lifecycle: (params: {
      props: GetProps<Props, DefaultProps>
      locale: Prefix extends TranslationPrefix
        ? PrefixedLocaleFunction<Prefix>
        : undefined
    }) => _Lifecycle,
  ) => Factory<Props, DefaultProps, _Lifecycle, Prefix>
  withRender: (
    render: FunctionComponent<{
      props: PropsWithChildren<GetProps<Props, DefaultProps>>
      lifecycle: Lifecycle
      locale: Prefix extends TranslationPrefix
        ? PrefixedLocaleFunction<Prefix>
        : undefined
      ref: ForwardedRef<FunctionComponent<Props>>
    }>,
  ) => Factory<Props, DefaultProps, Lifecycle, Prefix>
  withDefaultProps: <_DefaultProps extends Partial<Props>>(
    defaultProps: _DefaultProps,
  ) => Factory<Props, _DefaultProps, Lifecycle, Prefix>
}

function extendComponent<
  Props extends object = {},
  DefaultProps extends Partial<Props> = {},
  Lifecycle extends object | void = void,
  Prefix extends TranslationPrefix | undefined = undefined,
>(
  render: FunctionComponent<
    PropsWithChildren<{
      props: PropsWithChildren<Props>
      lifecycle: Lifecycle
      locale: Prefix extends TranslationPrefix
        ? PrefixedLocaleFunction<Prefix>
        : undefined
      ref: ForwardedRef<FunctionComponent<Props>>
    }>
  >,
  useLifecycle: (params: {
    props: Props
    locale: Prefix extends TranslationPrefix
      ? PrefixedLocaleFunction<Prefix>
      : undefined
  }) => Lifecycle,
  prefix: Prefix,
  defaultProps: DefaultProps,
): Factory<Props, DefaultProps, Lifecycle, Prefix> {
  const useLocale = prefix ? createLocaleHook(prefix) : () => {}

  const Component = React.memo(
    React.forwardRef<
      FunctionComponent<PropsWithChildren<Props>>,
      PropsWithChildren<Props>
    >((props, ref) => {
      const locale = useLocale() as Prefix extends TranslationPrefix
        ? PrefixedLocaleFunction<Prefix>
        : undefined
      const lifecycle = useLifecycle({ props, locale })

      return render({
        props,
        locale,
        lifecycle,
        ref,
        children: props.children,
      })
    }),
  ) as unknown as Factory<Props, DefaultProps, Lifecycle, Prefix>
  Component.defaultProps = defaultProps

  Component.withLocale = (_locale) =>
    extendComponent(render as any, useLifecycle as any, _locale, defaultProps)
  Component.withLifecycle = (_lifecycle) =>
    extendComponent(render as any, _lifecycle as any, prefix, defaultProps)
  Component.withRender = (_render) =>
    extendComponent(_render as any, useLifecycle, prefix, defaultProps)
  Component.withDefaultProps = (_defaultProps) =>
    extendComponent(render, useLifecycle, prefix, _defaultProps)

  return Component
}

export function buildComponent<Props extends {} = {}>(): Factory<Props> {
  return extendComponent<Props>(
    () => null,
    () => {},
    undefined,
    {},
  )
}
